import { useCallback, useEffect, useMemo, useState } from "react";
import { insertRow, listRows, updateRow } from "@/lib/adminApi";
import {
  FARMER_STATUS,
  TRAINING_STATUS,
  VERIFICATION_STATUS,
  metaFor,
  optionsFrom,
  type Farmer,
} from "@/lib/adminModules";
import {
  DataTable,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatRupees } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

const EMPTY_FARMER = {
  name: "",
  village: "",
  district: "",
  crop: "",
  farming_for: "",
  phone: "",
  email: "",
  status: "application",
  verification: "pending",
};

export default function AdminFarmers() {
  const [rows, setRows] = useState<Farmer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_FARMER });
  const [saving, setSaving] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<Farmer>("farmers", "name"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load farmers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<Farmer>) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("farmers", id, patch);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const createFarmer = async () => {
    if (!draft.name.trim()) {
      setError("A farmer name is required.");
      return;
    }
    setSaving(true);
    try {
      const created = await insertRow<Farmer>("farmers", {
        ...draft,
        name: draft.name.trim(),
        rating: 0,
        total_bookings: 0,
        total_revenue: 0,
      });
      setRows((list) => [created, ...list]);
      setCreateOpen(false);
      setDraft({ ...EMPTY_FARMER });
      notify(`${created.name} added to the network.`);
    } catch {
      setError("We couldn't create that farmer. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (verificationFilter !== "all" && row.verification !== verificationFilter) return false;
      if (!query) return true;
      return [row.name, row.village || "", row.crop || "", row.phone || ""]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [rows, search, statusFilter, verificationFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;
  const revenue = rows.reduce((sum, row) => sum + Number(row.total_revenue || 0), 0);

  const columns: Column<Farmer>[] = [
    {
      key: "name",
      header: "Farmer",
      primary: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full overflow-hidden bg-background-100 shrink-0 flex items-center justify-center text-primary-700">
            {row.image ? (
              <img src={row.image} alt={row.name} className="w-full h-full object-cover object-top" />
            ) : (
              <i className="ri-user-star-line"></i>
            )}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
            <p className="text-xs text-foreground-500 mt-0.5">{row.crop || "—"}</p>
          </div>
        </div>
      ),
    },
    {
      key: "village",
      header: "Village",
      render: (row) => (
        <span className="text-sm text-foreground-700 whitespace-nowrap">{row.village || "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => {
        const m = metaFor(FARMER_STATUS, row.status);
        return <StatusPill label={m.label} tone={m.tone} />;
      },
    },
    {
      key: "verification",
      header: "Verification",
      render: (row) => {
        const m = metaFor(VERIFICATION_STATUS, row.verification);
        return <StatusPill label={m.label} tone={m.tone} />;
      },
    },
    {
      key: "bookings",
      header: "Bookings",
      render: (row) => <span className="text-sm text-foreground-700">{row.total_bookings}</span>,
    },
    {
      key: "revenue",
      header: "Revenue",
      render: (row) => (
        <span className="text-sm text-foreground-800 whitespace-nowrap">
          {formatRupees(row.total_revenue)}
        </span>
      ),
    },
    {
      key: "rating",
      header: "Rating",
      render: (row) => (
        <span className="inline-flex items-center gap-1 text-sm text-foreground-800">
          <i className="ri-star-fill text-accent-500"></i>
          {row.rating}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Farm Network"
        title="Farmers"
        subtitle="Every grower in the Rythugaru network — their farms, verification state, training and earnings."
        actions={
          <button type="button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
            <i className="ri-user-add-line"></i> New Farmer
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Total farmers", value: rows.length, icon: "ri-user-star-line", tone: "primary" },
          {
            label: "Active",
            value: rows.filter((row) => row.status === "active").length,
            icon: "ri-checkbox-circle-line",
            tone: "primary",
          },
          {
            label: "Awaiting verification",
            value: rows.filter((row) => row.verification !== "verified").length,
            icon: "ri-shield-check-line",
            tone: "accent",
          },
          {
            label: "Network revenue",
            value: formatRupees(revenue),
            icon: "ri-money-rupee-circle-line",
            tone: "secondary",
          },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search farmers by name, village, crop…" />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...optionsFrom(FARMER_STATUS)]}
          />
          <Select
            label="Verification"
            value={verificationFilter}
            onChange={setVerificationFilter}
            options={[{ value: "all", label: "All verification" }, ...optionsFrom(VERIFICATION_STATUS)]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">
            {filtered.length} farmers
          </span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading farmers…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-user-star-line"
            title={rows.length === 0 ? "No farmers yet" : "No farmers match"}
            message={
              rows.length === 0
                ? "Approved farmer applications will appear here."
                : "Try a different search or clear the filters."
            }
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.name || "Farmer"}
        subtitle="Farmer profile"
      >
        {selected ? (
          <>
            <div className="flex items-center gap-4">
              <span className="w-16 h-16 rounded-full overflow-hidden bg-background-100 shrink-0 flex items-center justify-center text-primary-700">
                {selected.image ? (
                  <img src={selected.image} alt={selected.name} className="w-full h-full object-cover object-top" />
                ) : (
                  <i className="ri-user-star-line text-2xl"></i>
                )}
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill {...metaFor(FARMER_STATUS, selected.status)} />
                  <StatusPill {...metaFor(VERIFICATION_STATUS, selected.verification)} />
                </div>
                <p className="text-sm text-foreground-600 mt-2">
                  {selected.village} · {selected.farming_for}
                </p>
              </div>
            </div>

            {selected.quote ? (
              <p className="font-heading text-lg text-foreground-900 italic border-l-2 border-accent-500 pl-4">
                “{selected.quote}”
              </p>
            ) : null}

            {selected.intro ? (
              <p className="text-sm text-foreground-700 leading-relaxed">{selected.intro}</p>
            ) : null}

            {selected.specialties && selected.specialties.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selected.specialties.map((item) => (
                  <span
                    key={item}
                    className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full"
                  >
                    {item}
                  </span>
                ))}
              </div>
            ) : null}

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-background-100 border border-background-200 rounded-md p-3">
                <p className="font-heading text-xl font-semibold text-foreground-950">
                  {selected.total_bookings}
                </p>
                <p className="text-[11px] text-foreground-500 mt-0.5">Bookings</p>
              </div>
              <div className="bg-background-100 border border-background-200 rounded-md p-3">
                <p className="font-heading text-xl font-semibold text-foreground-950">
                  {formatRupees(selected.total_revenue)}
                </p>
                <p className="text-[11px] text-foreground-500 mt-0.5">Revenue</p>
              </div>
              <div className="bg-background-100 border border-background-200 rounded-md p-3">
                <p className="font-heading text-xl font-semibold text-foreground-950">
                  {selected.rating}
                </p>
                <p className="text-[11px] text-foreground-500 mt-0.5">Rating</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Farm status">
                <select
                  value={selected.status}
                  onChange={(event) => update(selected.id, { status: event.target.value })}
                  className={fieldInput}
                >
                  {optionsFrom(FARMER_STATUS).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Verification">
                <select
                  value={selected.verification}
                  onChange={(event) => update(selected.id, { verification: event.target.value })}
                  className={fieldInput}
                >
                  {optionsFrom(VERIFICATION_STATUS).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Training">
                <select
                  value={selected.training_status}
                  onChange={(event) => update(selected.id, { training_status: event.target.value })}
                  className={fieldInput}
                >
                  {optionsFrom(TRAINING_STATUS).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Contact">
                <div className="flex flex-col gap-2">
                  {selected.phone ? (
                    <a href={`tel:${selected.phone}`} className={btnGhost}>
                      <i className="ri-phone-line"></i> {selected.phone}
                    </a>
                  ) : null}
                  {selected.email ? (
                    <a href={`mailto:${selected.email}`} className={btnGhost}>
                      <i className="ri-mail-line"></i> Email farmer
                    </a>
                  ) : null}
                </div>
              </Field>
            </div>

            {selected.bio && selected.bio.length > 0 ? (
              <Field label="About">
                <div className="space-y-2">
                  {selected.bio.map((line) => (
                    <p key={line} className="text-sm text-foreground-700 leading-relaxed">
                      {line}
                    </p>
                  ))}
                </div>
              </Field>
            ) : null}
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add a new farmer"
        subtitle="New record"
        footer={
          <>
            <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>
              Cancel
            </button>
            <button type="button" onClick={createFarmer} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Create farmer
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Farmer name">
            <input
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              placeholder="e.g. Ramesh Naidu"
              className={textInput}
            />
          </TextField>
          <TextField label="Primary crop">
            <input
              value={draft.crop}
              onChange={(event) => setDraft({ ...draft, crop: event.target.value })}
              placeholder="e.g. Paddy"
              className={textInput}
            />
          </TextField>
          <TextField label="Village">
            <input
              value={draft.village}
              onChange={(event) => setDraft({ ...draft, village: event.target.value })}
              placeholder="e.g. Ravulapalem"
              className={textInput}
            />
          </TextField>
          <TextField label="District">
            <input
              value={draft.district}
              onChange={(event) => setDraft({ ...draft, district: event.target.value })}
              placeholder="e.g. East Godavari"
              className={textInput}
            />
          </TextField>
          <TextField label="Farming for">
            <input
              value={draft.farming_for}
              onChange={(event) => setDraft({ ...draft, farming_for: event.target.value })}
              placeholder="e.g. 22 years"
              className={textInput}
            />
          </TextField>
          <TextField label="Phone">
            <input
              value={draft.phone}
              onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
              placeholder="+91 98765 43210"
              className={textInput}
            />
          </TextField>
          <TextField label="Email">
            <input
              type="email"
              value={draft.email}
              onChange={(event) => setDraft({ ...draft, email: event.target.value })}
              placeholder="farmer@example.com"
              className={textInput}
            />
          </TextField>
          <TextField label="Status">
            <select
              value={draft.status}
              onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              className={fieldInput}
            >
              {optionsFrom(FARMER_STATUS).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </TextField>
          <TextField label="Verification">
            <select
              value={draft.verification}
              onChange={(event) => setDraft({ ...draft, verification: event.target.value })}
              className={fieldInput}
            >
              {optionsFrom(VERIFICATION_STATUS).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </TextField>
        </div>
      </Modal>
      {node}
    </div>
  );
}